import Checkbox, { CheckboxProps } from 'components/Checkbox'
import { useInputMask } from 'hooks/input-mask'
import { action } from 'mobx'
import { observer } from 'mobx-react'
import React, { useCallback } from 'react'
import { MaskBlock } from 'utils/input-mask-util'
import { isNotNil } from 'utils/object-util'
import styles from './control.sass'
import DateInput, { DateInputProps } from './DateInput'
import Form from './Form'
import FormActions from './FormActions'
import { FormColumn, FormColumns } from './FormColumns'
import FormGroup, { FormGroupProps } from './FormGroup'
import FormRow from './FormRow'
import FormSection from './FormSection'
import Help from './Help'
import NumericInput, { NumericInputProps } from './NumericInput'
import { SelectInput, SelectInputProps } from './Select'
import TextAreaInput, { TextAreaProps } from './TextArea'
import TextInput, { TextInputProps } from './TextInput'
import { SwitchCheckbox, Props as SwitchCheckboxProps } from '../SwitchCheckbox'
import cx from 'classnames'

/**
 * Form Group container.
 */
export const FormGroupContainer = <T, K extends keyof T, V>({
  children,
  model,
  error,
  help,
  field,
  ...rest
}: FormGroupContainerProps<T, K, V>) => {
  return (
    <FormGroup {...rest}>
      {children}
      <Help error={error} help={help} />
    </FormGroup>
  )
}

interface FormGroupContainerProps<T, K extends keyof T, V>
  extends Partial<WithModelField<T, K, V>>,
    FormGroupProps {
  help?: React.ReactNode
  error?: string
}

/**
 * Checkbox Input container.
 */
export const CheckboxInputContainer = observer(function CheckboxInputContainer<
  T,
  K extends keyof T,
>({
  model,
  field,
  error,
  help,
  label,
  ...props
}: CheckboxInputContainerProps<T, K>) {
  const value = model[field] as unknown as boolean
  const onChange = useCallback(
    action((checked: boolean) => {
      ;(model as any)[field] = checked
    }),
    [model, field],
  )
  return (
    <div className={cx(styles.container, styles.checkbox)}>
      <Checkbox checked={value} onChange={onChange} {...props} />
      <div className={styles.checkboxSpacing}>
        <label
          title={label}
          className={cx(styles.label, styles.checkboxLabel)}
          onClick={() => onChange(!value)}
        >
          {label}
        </label>
        <Help error={error} help={help} />
      </div>
    </div>
  )
})

interface CheckboxInputContainerProps<T, K extends keyof T>
  extends WithModelField<T, K, boolean>,
    Omit<CheckboxProps, 'value' | 'onChange'> {
  label?: string
  help?: string
  error?: string
}

/**
 * SwitchCheckbox Input container.
 */
export const SwitchCheckboxInputContainer = observer(
  function SwitchCheckboxInputContainer<T, K extends keyof T>({
    model,
    field,
    error,
    help,
    label,
    ...props
  }: SwitchCheckboxInputContainerProps<T, K>) {
    const value = model[field] as unknown as boolean
    const onChange = useCallback(
      action((checked: boolean) => {
        ;(model as any)[field] = checked
      }),
      [model, field],
    )
    return (
      <FormGroupContainer
        label={label}
        error={error}
        model={model}
        field={field}
      >
        <div className={styles.switchCheckboxContainer}>
          {help && <div className={styles.switchCheckboxHelp}>{help}</div>}
          <SwitchCheckbox
            className={styles.switchCheckbox}
            value={value}
            onChange={onChange}
            {...props}
          />
        </div>
      </FormGroupContainer>
    )
  },
)

interface SwitchCheckboxInputContainerProps<T, K extends keyof T>
  extends WithModelField<T, K, boolean>,
    Omit<SwitchCheckboxProps, 'value' | 'onChange'> {
  label?: string
  help?: string
  error?: string
  description?: string
}

/**
 * Numeric Input container.
 */
export const NumericInputContainer = observer(function NumericInputContainer<
  T,
  K extends keyof T,
>({
  model,
  field,
  error,
  help,
  label,
  ...props
}: NumericInputContainerProps<T, K>) {
  const value = model[field] as unknown as number
  const onChange = useCallback(
    action((newNumber: number | null) => {
      // Set the value on the model; fall back to zero in case of `null`.
      ;(model as any)[field] = newNumber ?? 0
    }),
    [model, field],
  )
  return (
    <FormGroupContainer
      label={label}
      error={error}
      help={help}
      model={model}
      field={field}
    >
      <NumericInput value={value} mode="draft" onChange={onChange} {...props} />
    </FormGroupContainer>
  )
})

interface NumericInputContainerProps<T, K extends keyof T>
  extends WithModelField<T, K, number>,
    Omit<NumericInputProps, 'value' | 'onChange'> {
  className?: string
  label?: string
  help?: string
  error?: string
  placeholder?: string
}

/**
 * Text Input container.
 */
export const TextInputContainer = observer(function TextInputContainer<
  T,
  K extends keyof T,
>({
  visual,
  staticText,
  className,
  model,
  field,
  error,
  help,
  label,
  sublabel,
  name,
  mask,
  ...props
}: TextInputContainerProps<T, K>) {
  const value = model[field] as unknown as string
  const setValue = useCallback(
    action((newValue: string) => {
      ;(model as any)[field] = newValue
    }),
    [model, field],
  )
  const { maskedValue, onChange: maskedOnChange } = useInputMask({
    mask,
    value: isNotNil(value) ? value : '',
    onChange: setValue,
  })
  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      maskedOnChange(e.target.value)
    },
    [maskedOnChange],
  )
  return (
    <FormGroupContainer
      label={label}
      sublabel={sublabel}
      error={error}
      help={help}
      model={model}
      field={field}
      htmlFor={name}
    >
      <TextInput
        visual={visual}
        staticText={staticText}
        className={className}
        value={maskedValue}
        onChange={onChange}
        status={error ? 'danger' : 'default'}
        name={name}
        {...props}
      />
    </FormGroupContainer>
  )
})

interface TextInputContainerProps<T, K extends keyof T>
  extends WithModelField<T, K, string>,
    Omit<TextInputProps, 'value' | 'onChange'> {
  className?: string
  label?: string
  sublabel?: string
  help?: string
  error?: string
  placeholder?: string
  hexColor?: boolean
  mask?: MaskBlock[]
}

/**
 * Date Input container.
 */
export const DateInputContainer = observer(function DateInputContainer<
  T,
  K extends keyof T,
>({
  model,
  field,
  error,
  help,
  label,
  ...props
}: DateInputContainerProps<T, K>) {
  const value = model[field] as unknown as Date | null | undefined
  const onChange = useCallback(
    action((value: Date) => {
      ;(model as any)[field] = value
    }),
    [model, field],
  )
  return (
    <FormGroupContainer
      label={label}
      error={error}
      help={help}
      model={model}
      field={field}
    >
      <DateInput
        value={value}
        onChange={onChange}
        status={error ? 'danger' : 'default'}
        {...props}
      />
    </FormGroupContainer>
  )
})

interface DateInputContainerProps<T, K extends keyof T>
  extends WithModelField<T, K, Date>,
    Omit<DateInputProps, 'value' | 'onChange'> {
  label?: string
  help?: string
  error?: string
  placeholder?: string
}

/**
 * Text Input container.
 */
export const TextAreaContainer = observer(function TextAreaContainer<
  T,
  K extends keyof T,
  V,
>({
  className,
  model,
  field,
  error,
  help,
  label,
  rows,
  focusOnError = false,
  ...props
}: TextAreaContainerProps<T, K, V>) {
  const value = model[field] as unknown as string
  const textAreaRef = React.useRef<HTMLTextAreaElement | null>(null)

  React.useEffect(() => {
    if (focusOnError && error && textAreaRef.current) {
      textAreaRef.current.focus()
      textAreaRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }, [error])

  const onChange = useCallback(
    action((e: React.ChangeEvent<HTMLTextAreaElement>) => {
      ;(model as any)[field] = e.target.value
    }),
    [model, field],
  )
  return (
    <FormGroupContainer
      label={label}
      error={error}
      help={help}
      model={model}
      field={field}
    >
      <TextAreaInput
        className={className}
        value={isNotNil(value) ? value : ''}
        onChange={onChange}
        rows={rows}
        ref={textAreaRef}
        status={error ? 'danger' : 'default'}
        {...props}
      />
    </FormGroupContainer>
  )
})

interface TextAreaContainerProps<T, K extends keyof T, V>
  extends WithModelField<T, K, V>,
    Omit<TextAreaProps, 'value' | 'onChange'> {
  className?: string
  label?: string
  help?: string
  error?: string
  rows?: number
  placeholder?: string
  focusOnError?: boolean
}

export const SelectContainer = observer(function SelectContainer<
  T,
  K extends keyof T,
  V,
>({
  className,
  model,
  field,
  placeholder,
  label,
  help,
  error,
  items,
  ...rest
}: SelectContainerProps<T, K, V>) {
  const value = model[field] as unknown as string
  const onChange = useCallback(
    action((e: React.ChangeEvent<HTMLSelectElement>) => {
      ;(model as any)[field] = items[parseInt(e.target.value, 10)][0]
    }),
    [model, field],
  )
  return (
    <FormGroupContainer
      label={label}
      error={error}
      help={help}
      model={model}
      field={field}
    >
      <SelectInput
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        items={items}
        {...rest}
      />
    </FormGroupContainer>
  )
})

interface SelectContainerProps<T, K extends keyof T, V>
  extends WithModelField<T, K, V>,
    Omit<SelectInputProps, 'value' | 'onChange'> {
  items: Array<SelectItem<V>>
  className?: string
  label?: string
  help?: string
  error?: string
  placeholder?: string
}

type SelectItem<V> = [V] | [V, string]

export interface WithModelField<T, K extends keyof T, V> {
  model: T[K] extends V ? T : never
  field: T[K] extends V ? K : never
}

export {
  Form,
  FormGroup,
  FormRow,
  FormColumns,
  FormColumn,
  FormSection,
  FormActions,
  Help,
}
